import { useState } from "react";
import { IconButton, ChoiceGroup, IChoiceGroupOption, PrimaryButton, TextField } from "@fluentui/react";
import { useTranslation } from "react-i18next";

export type FeedbackPayload = {
    rating: "positive" | "negative";
    category: string;
    remark: string;
    status: "pending" | "in progress" | "completed";
};

interface AnswerFeedbackProps {
    onFeedbackClicked?: (result: FeedbackPayload | undefined) => void;
}

export const AnswerFeedback = ({ onFeedbackClicked }: AnswerFeedbackProps) => {
    const { t } = useTranslation();
    const [feedback, setFeedback] = useState<string>();
    const [showNegativeFeedbackForm, setShowNegativeFeedbackForm] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
    const [comment, setComment] = useState<string>("");

    const negativeFeedbackOptions: IChoiceGroupOption[] = [
        { key: "falsch", text: t("categories.falsch") },
        { key: "fehlt", text: t("categories.fehlt") },
        { key: "funktion", text: t("categories.funktion") },
        { key: "sonstiges", text: t("categories.sonstiges") },
    ];

    return (
        <div>
            <IconButton
                style={{ color: "black" }}
                iconProps={{ iconName: feedback === "positive" ? "LikeSolid" : "Like" }}
                title={t("answer.ratePositive")}
                ariaLabel={t("answer.ratePositive")}
                onClick={() => {
                    if (feedback === "positive") {
                        setFeedback(undefined);
                        setShowNegativeFeedbackForm(false);
                        onFeedbackClicked?.(undefined);
                    } else {
                        onFeedbackClicked?.({ rating: "positive", category: "", remark: "", status: "completed" });
                        setFeedback("positive");
                        setShowNegativeFeedbackForm(false);
                    }
                }}
            />

            <IconButton
                style={{ color: "black" }}
                iconProps={{ iconName: feedback === "negative" ? "DislikeSolid" : "Dislike" }}
                title={t("answer.rateNegative")}
                ariaLabel={t("answer.rateNegative")}
                onClick={() => {
                    if (feedback === "negative") {
                        setFeedback(undefined);
                        setShowNegativeFeedbackForm(false);
                        onFeedbackClicked?.(undefined);
                    } else {
                        setFeedback("negative");
                        setShowNegativeFeedbackForm(true);
                    }
                }}
            />

            {showNegativeFeedbackForm && (
                <div className="mt-2 bg-gray-50 p-3 border rounded-md space-y-3">
                    <div className="text-sm font-semibold">
                        {t("answer.whyNotHelpful")}
                    </div>
                    <ChoiceGroup
                        options={negativeFeedbackOptions}
                        selectedKey={selectedCategory}
                        onChange={(e, option) => setSelectedCategory(option?.key)}
                        required
                    />
                    <TextField
                        label={t("answer.remark")}
                        multiline
                        required
                        value={comment}
                        onChange={(_, val) => setComment(val || "")}
                    />
                    <PrimaryButton
                        text={t("answer.submit")}
                        onClick={() => {
                            if (selectedCategory && comment.trim()) {
                                const detailedFeedback = {
                                    rating: "negative" as const,
                                    category: selectedCategory,
                                    remark: comment.trim(),
                                    status: "pending" as const,
                                };
                                onFeedbackClicked?.(detailedFeedback);
                                setShowNegativeFeedbackForm(false);
                                setComment("");
                                setSelectedCategory(undefined);
                            }
                        }}
                        disabled={!selectedCategory || !comment.trim()}
                    />
                </div>
            )}
        </div>
    );
}; 